/**
 * DepartmentAdmin — Spectropharma org chart + position holders (V18).
 *
 * Owner: Phase 7+ — Department hierarchy.
 *
 * Renders the department tree on the left, and on selection shows the
 * positions held within that department (DEPARTMENT_HEAD, QA_MANAGER,
 * AUTHOR, etc.) with the user_id holding each role + backup delegate.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PageHero } from '@edms/design-system';
import { useDepartments, useDepartmentPositions, type Department, type DepartmentPosition } from '../../api/orgHooks';
import { NewDepartmentDialog } from './NewDepartmentDialog';
import { RotatePositionDialog } from './RotatePositionDialog';
import styles from './DepartmentAdmin.module.css';

interface DeptNode extends Department {
  children: DeptNode[];
  depth: number;
}

function buildTree(flat: Department[]): DeptNode[] {
  const byId = new Map<string, DeptNode>();
  for (const d of flat) byId.set(d.departmentId, { ...d, children: [], depth: 0 });
  const roots: DeptNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      const p = byId.get(node.parentId)!;
      node.depth = p.depth + 1;
      p.children.push(node);
    } else {
      roots.push(node);
    }
  }
  function setDepth(n: DeptNode, depth: number) {
    n.depth = depth;
    n.children.sort((a, b) => a.name.localeCompare(b.name));
    n.children.forEach((c) => setDepth(c, depth + 1));
  }
  roots.sort((a, b) => a.name.localeCompare(b.name));
  roots.forEach((r) => setDepth(r, 0));
  return roots;
}

function flatten(tree: DeptNode[], out: DeptNode[] = []): DeptNode[] {
  for (const n of tree) { out.push(n); flatten(n.children, out); }
  return out;
}

const KIND_LABEL: Record<string, string> = {
  DEPARTMENT_HEAD: 'Department head',
  DEPUTY_HEAD:     'Deputy head',
  QA_MANAGER:      'QA manager',
  AUTHOR:          'Author',
  REVIEWER:        'Reviewer',
  DOC_CONTROLLER:  'Document controller',
  TRAINEE:         'Trainee',
};

export function DepartmentAdmin() {
  const { t } = useTranslation();
  const depsQ = useDepartments();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const positionsQ = useDepartmentPositions(selectedId ?? undefined);
  const [newDeptOpen, setNewDeptOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateKind, setRotateKind] = useState<DepartmentPosition['positionKind']>('DEPARTMENT_HEAD');

  const tree = useMemo(() => buildTree(depsQ.data?.items ?? []), [depsQ.data]);
  const flat = useMemo(() => flatten(tree), [tree]);
  const selected = flat.find((n) => n.departmentId === selectedId) ?? null;

  return (
    <div className={styles.page}>
      <PageHero
        eyebrow={t('deptAdmin.eyebrow', { defaultValue: 'Admin' })}
        title={t('deptAdmin.title', { defaultValue: 'Departments & positions' })}
        subtitle={t('deptAdmin.subtitle', {
          defaultValue:
            'Spectropharma org chart. Pick a department to see who holds each role — these positions drive department-routed review (DEPARTMENT_HEAD → QA_MANAGER) when a new document is submitted.',
        })}
        actions={
          <Button variant="primary" onClick={() => setNewDeptOpen(true)} leadingIcon="+">
            {t('deptAdmin.newDept', { defaultValue: 'New department' })}
          </Button>
        }
      />

      <NewDepartmentDialog
        isOpen={newDeptOpen}
        onClose={() => setNewDeptOpen(false)}
        onCreated={(d) => setSelectedId(d.departmentId)}
        departments={depsQ.data?.items ?? []}
      />
      {selected && (
        <RotatePositionDialog
          isOpen={rotateOpen}
          onClose={() => setRotateOpen(false)}
          departmentId={selected.departmentId}
          departmentName={selected.name}
          initialKind={rotateKind}
          onRotated={() => { /* invalidated by hook */ }}
        />
      )}

      <div className={styles.body}>
        <aside className={styles.tree} aria-label="Department tree">
          <h2 className={styles.sectionTitle}>{t('deptAdmin.tree', { defaultValue: 'Org chart' })}</h2>
          {depsQ.isLoading && <div className={styles.empty}>{t('common.loading', { defaultValue: 'Loading…' })}</div>}
          <ul className={styles.treeList}>
            {flat.map((n) => (
              <li key={n.departmentId}>
                <button
                  type="button"
                  className={`${styles.treeItem} ${selectedId === n.departmentId ? styles.treeItemActive : ''}`}
                  onClick={() => setSelectedId(n.departmentId)}
                  aria-current={selectedId === n.departmentId ? 'true' : undefined}
                  style={{ paddingInlineStart: 12 + n.depth * 16 }}
                >
                  <span className={styles.treeCode}>{n.code}</span>
                  <span className={styles.treeName}>{n.name}</span>
                  {n.siteCode && <span className={styles.treeSite}>{n.siteCode}</span>}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className={styles.detail}>
          {!selected && (
            <div className={styles.empty}>{t('deptAdmin.pick', { defaultValue: 'Pick a department on the left.' })}</div>
          )}
          {selected && (
            <>
              <h2 className={styles.sectionTitle}>
                {selected.name}
                <span className={styles.code}> · {selected.code}</span>
              </h2>
              <dl className={styles.metaGrid}>
                {selected.siteCode   && <div><dt>{t('deptAdmin.field.site',   { defaultValue: 'Site' })}</dt>          <dd>{selected.siteCode}</dd></div>}
                {selected.costCenter && <div><dt>{t('deptAdmin.field.cc',     { defaultValue: 'Cost centre' })}</dt>   <dd>{selected.costCenter}</dd></div>}
                <div><dt>{t('deptAdmin.field.active', { defaultValue: 'Active' })}</dt><dd>{selected.isActive ? 'yes' : 'no'}</dd></div>
              </dl>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
                <h3 className={styles.sectionTitleSm} style={{ marginTop: 0 }}>{t('deptAdmin.positions', { defaultValue: 'Positions (current holders)' })}</h3>
                <Button variant="secondary" size="sm" onClick={() => { setRotateKind('DEPARTMENT_HEAD'); setRotateOpen(true); }}>
                  {t('deptAdmin.rotate', { defaultValue: 'Rotate position' })}
                </Button>
              </div>
              {positionsQ.isLoading && <div className={styles.empty}>{t('common.loading', { defaultValue: 'Loading…' })}</div>}
              {positionsQ.data && positionsQ.data.items.length === 0 && (
                <div className={styles.empty}>{t('deptAdmin.noPositions', { defaultValue: 'No positions configured.' })}</div>
              )}
              {positionsQ.data && positionsQ.data.items.length > 0 && (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('deptAdmin.field.kind',   { defaultValue: 'Position' })}</th>
                      <th>{t('deptAdmin.field.holder', { defaultValue: 'Primary holder' })}</th>
                      <th>{t('deptAdmin.field.backup', { defaultValue: 'Backup / delegate' })}</th>
                      <th>{t('deptAdmin.field.since',  { defaultValue: 'Since' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positionsQ.data.items.map((p) => (
                      <tr key={p.positionId}>
                        <td><strong>{KIND_LABEL[p.positionKind] ?? p.positionKind}</strong></td>
                        <td>{p.userName}</td>
                        <td>{p.backupUserName ?? '—'}</td>
                        <td>{new Date(p.effectiveFromUtc).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
